<template>
    <v-dialog v-model="dialogVisible" max-width="550px">
        <v-card>
            <v-card-title class="success white--text">
                <v-icon left color="white">mdi-bank-transfer</v-icon>
                Platobné údaje
                <v-spacer></v-spacer>
                <v-btn icon color="white" @click="dialogVisible = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text class="pa-6">
                <v-alert type="info" outlined class="mb-4">
                    Investícia sa stane aktívnou až po pripísaní sumy na náš účet.
                    Použite prosím nižšie uvedené platobné údaje.
                </v-alert>

                <v-list>
                    <!-- IBAN -->
                    <v-list-item class="payment-item px-0">
                        <v-list-item-content>
                            <v-list-item-subtitle class="text-caption mb-1">Príjemca</v-list-item-subtitle>
                            <v-list-item-title class="text-body-1 font-weight-medium">{{ company.name }}</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>

                    <v-divider></v-divider>

                    <v-list-item class="payment-item px-0">
                        <v-list-item-content>
                            <v-list-item-subtitle class="text-caption mb-1">IBAN</v-list-item-subtitle>
                            <v-list-item-title class="text-h6 font-weight-bold primary--text">
                                {{ company.iban }}
                            </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <v-btn icon small @click="copyText(company.iban, 'IBAN')">
                                <v-icon small>mdi-content-copy</v-icon>
                            </v-btn>
                        </v-list-item-action>
                    </v-list-item>

                    <v-divider></v-divider>

                    <!-- Amount -->
                    <v-list-item v-if="amount" class="payment-item px-0">
                        <v-list-item-content>
                            <v-list-item-subtitle class="text-caption mb-1">Suma na úhradu</v-list-item-subtitle>
                            <v-list-item-title class="text-h5 success--text font-weight-bold">
                                {{ formatCurrency(amount) }}
                            </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <v-btn icon small @click="copyText(String(amount), 'Suma')">
                                <v-icon small>mdi-content-copy</v-icon>
                            </v-btn>
                        </v-list-item-action>
                    </v-list-item>

                    <v-divider v-if="amount"></v-divider>

                    <!-- Variable Symbol -->
                    <v-list-item v-if="variableSymbol" class="payment-item px-0">
                        <v-list-item-content>
                            <v-list-item-subtitle class="text-caption mb-1">Variabilný symbol</v-list-item-subtitle>
                            <v-list-item-title class="text-h6 font-weight-bold warning--text">
                                {{ variableSymbol }}
                            </v-list-item-title>
                            <v-list-item-subtitle class="text-caption">
                                Toto je vaše používateľské ID – bez neho nevieme platbu priradiť.
                            </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                            <v-btn icon small @click="copyText(String(variableSymbol), 'Variabilný symbol')">
                                <v-icon small>mdi-content-copy</v-icon>
                            </v-btn>
                        </v-list-item-action>
                    </v-list-item>

                    <v-divider v-if="variableSymbol"></v-divider>

                    <!-- Note / Message -->
                    <v-list-item class="payment-item px-0">
                        <v-list-item-content>
                            <v-list-item-subtitle class="text-caption mb-1">Poznámka / správa pre
                                príjemcu</v-list-item-subtitle>
                            <v-list-item-title class="text-body-1">{{ company.paymentNote }}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <v-btn icon small @click="copyText(company.paymentNote, 'Poznámka')">
                                <v-icon small>mdi-content-copy</v-icon>
                            </v-btn>
                        </v-list-item-action>
                    </v-list-item>
                </v-list>

                <v-alert v-if="copySnackbar.show" :type="copySnackbar.color" dense outlined class="mt-4 mb-0">
                    {{ copySnackbar.message }}
                </v-alert>
            </v-card-text>

            <v-card-actions class="px-6 pb-6">
                <v-spacer></v-spacer>
                <v-btn color="primary" large @click="dialogVisible = false">
                    <v-icon left>mdi-check</v-icon>
                    Rozumiem
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { COMPANY } from '@/config/investment';
import { formatCurrency } from '@/utils/investment-math';

export default {
    name: "PaymentDetailsDialog",

    props: {
        value: {
            type: Boolean,
            default: false
        },
        amount: {
            type: [Number, String],
            default: null
        },
        variableSymbol: {
            type: [Number, String],
            default: null
        }
    },

    data() {
        return {
            company: { ...COMPANY },
            copySnackbar: {
                show: false,
                message: '',
                color: 'success'
            }
        };
    },

    computed: {
        dialogVisible: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            }
        }
    },

    methods: {
        formatCurrency,

        copyText(text, label) {
            const copy = (str) => {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    return navigator.clipboard.writeText(str);
                }
                const el = document.createElement('textarea');
                el.value = str;
                document.body.appendChild(el);
                el.select();
                document.execCommand('copy');
                document.body.removeChild(el);
                return Promise.resolve();
            };

            copy(text).then(() => {
                this.copySnackbar.message = `${label} skopírovaný do schránky`;
                this.copySnackbar.color = 'success';
                this.copySnackbar.show = true;
                setTimeout(() => { this.copySnackbar.show = false; }, 2500);
            }).catch(() => {
                this.copySnackbar.message = 'Kopírovanie zlyhalo';
                this.copySnackbar.color = 'error';
                this.copySnackbar.show = true;
                setTimeout(() => { this.copySnackbar.show = false; }, 2500);
            });
        }
    }
};
</script>

<style scoped>
.payment-item {
    min-height: 56px;
    padding-top: 8px;
    padding-bottom: 8px;
}
</style>
